import { useMemo, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { api, type KundeStamm } from '../lib/api';

// Kunden-Stammdaten: Ansprechpartner / E-Mail / Telefon / Notizen je Kunde.
// Die E-Mail ist Voraussetzung für "Kunden informieren" (Termin-Radar) —
// gepflegt wird sie hier vom Einkauf. Der Kundenname ist bewusst nicht
// editierbar (Umbenennen/Zusammenführen läuft separat über Hamdi).

type Entwurf = Pick<KundeStamm, 'contact' | 'email' | 'phone' | 'notes'>;

export default function Kunden() {
  const kunden = useApi(() => api.kunden.verwaltung(), []);
  const [suche, setSuche] = useState('');
  const [bearbeitet, setBearbeitet] = useState<Record<number, Entwurf>>({});
  const [speichert, setSpeichert] = useState<number | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const liste = useMemo(() => {
    const alle = kunden.data?.data ?? [];
    const t = suche.trim().toLowerCase();
    if (!t) return alle;
    return alle.filter(k =>
      k.name.toLowerCase().includes(t) || k.contact.toLowerCase().includes(t) || k.email.toLowerCase().includes(t));
  }, [kunden.data, suche]);

  const wert = (k: KundeStamm, feld: keyof Entwurf): string =>
    bearbeitet[k.id]?.[feld] ?? k[feld];

  const setze = (k: KundeStamm, feld: keyof Entwurf, v: string) =>
    setBearbeitet(b => ({
      ...b,
      [k.id]: { contact: wert(k, 'contact'), email: wert(k, 'email'), phone: wert(k, 'phone'), notes: wert(k, 'notes'), [feld]: v },
    }));

  const speichern = async (k: KundeStamm) => {
    const e = bearbeitet[k.id];
    if (!e) return;
    setSpeichert(k.id);
    setFehler(null);
    try {
      await api.kunden.update(k.id, e);
      setBearbeitet(b => { const { [k.id]: _weg, ...rest } = b; return rest; });
      kunden.reload();
    } catch (err) {
      const body = (err as { body?: { error?: string } }).body;
      setFehler(`${k.name}: ${body?.error ?? 'Speichern fehlgeschlagen'}`);
    } finally {
      setSpeichert(null);
    }
  };

  const mitEmail = (kunden.data?.data ?? []).filter(k => k.email).length;
  const gesamt = kunden.data?.data.length ?? 0;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Kunden</h1>
          <div className="text-sm text-slate-500">
            Kontaktdaten für „Kunden informieren" — {mitEmail}/{gesamt} mit E-Mail
          </div>
        </div>
        <input className="input ml-auto w-full sm:w-64" placeholder="🔍 Kunde suchen …"
               value={suche} onChange={e => setSuche(e.target.value)} />
      </div>

      {fehler && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{fehler}</div>}
      {kunden.loading && <div className="text-slate-500 py-8 text-center">Lädt …</div>}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
        <table className="w-full text-[13px] min-w-[900px]">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs text-slate-600 uppercase">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Kunde</th>
              <th className="text-left px-3 py-2 font-medium">Ansprechpartner</th>
              <th className="text-left px-3 py-2 font-medium">E-Mail</th>
              <th className="text-left px-3 py-2 font-medium">Telefon</th>
              <th className="text-left px-3 py-2 font-medium">Notizen</th>
              <th className="text-right px-3 py-2 font-medium">Offen</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {liste.map(k => (
              <tr key={k.id} className={`hover:bg-slate-50 ${!k.active ? 'opacity-50' : ''}`}>
                <td className="px-3 py-1.5 font-medium whitespace-nowrap">
                  {k.name}{!k.active && <span className="text-xs text-slate-400 ml-1">(inaktiv)</span>}
                </td>
                <td className="px-2 py-1.5">
                  <input className="input !py-1 w-full min-w-[130px]" value={wert(k, 'contact')}
                         onChange={e => setze(k, 'contact', e.target.value)} />
                </td>
                <td className="px-2 py-1.5">
                  <input className={`input !py-1 w-full min-w-[190px] ${!wert(k, 'email') ? 'border-amber-300 bg-amber-50' : ''}`}
                         type="email" placeholder="kunde@firma.de" value={wert(k, 'email')}
                         onChange={e => setze(k, 'email', e.target.value)} />
                </td>
                <td className="px-2 py-1.5">
                  <input className="input !py-1 w-full min-w-[120px]" value={wert(k, 'phone')}
                         onChange={e => setze(k, 'phone', e.target.value)} />
                </td>
                <td className="px-2 py-1.5">
                  <input className="input !py-1 w-full min-w-[140px]" value={wert(k, 'notes')}
                         onChange={e => setze(k, 'notes', e.target.value)} />
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">{k.offene_teile || ''}</td>
                <td className="px-2 py-1.5 text-right">
                  {bearbeitet[k.id] && (
                    <button onClick={() => speichern(k)} disabled={speichert === k.id}
                            className="bg-brand text-white rounded-lg px-3 py-1 text-xs font-medium hover:opacity-90 disabled:opacity-50">
                      {speichert === k.id ? '…' : 'Speichern'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!kunden.loading && liste.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-slate-500">Keine Kunden gefunden.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
